(() => {
  const ensureFendoSizeOptions = () => {
    if (window.FendoSizeOptions) return window.FendoSizeOptions;

    const aliases = new Set([
      'size', 'sizes', 'grosse', 'groesse', 'grossen', 'groessen',
      'schuhgrosse', 'schuhgroesse', 'schuhgrossen', 'schuhgroessen',
      'grosseschuh', 'groesseschuh', 'grosseschuhe', 'groesseschuhe',
      'shoesize', 'shoesizes', 'sizeshoe', 'sizesshoes', 'shoessize',
      'kleidergrosse', 'kleidergroesse', 'kleidungsgrosse', 'kleidungsgroesse',
      'konfektionsgrosse', 'konfektionsgroesse', 'anzuggrosse', 'anzuggroesse',
      'hosengrosse', 'hosengroesse', 'hemdgrosse', 'hemdgroesse',
      'clothingsize', 'garmentsize', 'dresssize', 'suitsize', 'trousersize',
      'shirtsize', 'sizeeu', 'eusize', 'shoesizeeu', 'eushoesize',
      'grosseeu', 'groesseeu', 'eugrosse', 'eugroesse',
      'schuhgrosseeu', 'schuhgroesseeu', 'euschuhgrosse', 'euschuhgroesse',
      'taille', 'misura', 'talla', 'taglia'
    ]);

    const normalizeName = (value) => String(value ?? '')
      .normalize('NFKC')
      .toLowerCase()
      .replace(/ß/g, 'ss')
      .replace(/ö/g, 'o')
      .replace(/ä/g, 'a')
      .replace(/ü/g, 'u')
      .replace(/[\s._:()\/-]+/g, '');

    const isSizeName = (name) => aliases.has(normalizeName(name));
    const displayLabel = (name, locale = document.documentElement.lang) => {
      if (!isSizeName(name)) return String(name ?? '');
      // Set by snippets/fendo-head.liquid from the theme locale files.
      if (window.fendoStrings?.sizeLabel) return window.fendoStrings.sizeLabel;
      return String(locale || 'de').toLowerCase().split('-')[0] === 'en' ? 'Size' : 'Grösse';
    };

    window.FendoSizeOptions = Object.freeze({ normalizeName, isSizeName, displayLabel });
    return window.FendoSizeOptions;
  };

  ensureFendoSizeOptions();
  if (window.__fendoShopTheLookPatchInitialized) return;
  window.__fendoShopTheLookPatchInitialized = true;
  const inFlightRoots = new WeakSet();
  const recommendationSelector = '[data-shop-the-look-recommendations]';

  const getStorefrontLocale = () => window.Shopify?.locale || document.documentElement.lang || 'en';

  const getStorefrontRoot = () => {
    const configuredRoot = window.Shopify?.routes?.root;
    if (configuredRoot) return configuredRoot.endsWith('/') ? configuredRoot : `${configuredRoot}/`;

    const firstPathSegment = window.location.pathname.split('/').filter(Boolean)[0] || '';
    const locale = getStorefrontLocale().toLowerCase();
    const isLocalizedPath = firstPathSegment.toLowerCase() === locale || firstPathSegment.toLowerCase().split('-')[0] === locale.split('-')[0];
    return isLocalizedPath ? `/${firstPathSegment}/` : '/';
  };

  const storefrontUrl = (path = '') => `${getStorefrontRoot()}${String(path).replace(/^\/+/, '')}`;

  const normalizeRecommendationsUrl = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw)) return raw;

    const path = raw.replace(/^\/+/, '');
    const root = getStorefrontRoot();
    const rootPath = root.replace(/^\/+|\/+$/g, '');
    if (rootPath && (path === rootPath || path.startsWith(`${rootPath}/`))) return `/${path}`;
    return storefrontUrl(path);
  };

  const getRecommendationUrls = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return [];
    const primary = normalizeRecommendationsUrl(raw);
    const query = raw.includes('?') ? raw.slice(raw.indexOf('?') + 1) : '';
    const fallback = query ? `/recommendations/products.json?${query}` : '';
    return [...new Set([primary, fallback].filter(Boolean))];
  };

  const getMoneyFormatter = () => {
    try {
      const currency = window.Shopify?.currency?.active
        || document.querySelector('meta[property="product:price:currency"]')?.content
        || 'USD';
      return new Intl.NumberFormat(getStorefrontLocale(), {
        style: 'currency',
        currency
      });
    } catch (error) {
      return new Intl.NumberFormat('en', { style: 'currency', currency: 'USD' });
    }
  };

  const formatMoney = (cents) => getMoneyFormatter().format(Number(cents || 0) / 100);

  const getVariants = (item) => {
    try {
      return JSON.parse(item.querySelector('[data-shop-the-look-variants]')?.textContent || '[]');
    } catch (error) {
      return [];
    }
  };

  const getSelectedOptions = (item) => Array.from(item.querySelectorAll('[data-shop-the-look-option]')).map((select) => select.value);

  const findVariant = (item) => {
    const options = getSelectedOptions(item);
    if (options.length === 0 || options.some((option) => !option)) return null;
    return getVariants(item).find((variant) => variant.options?.every((value, index) => value === options[index])) || null;
  };

  const selectFirstAvailableVariant = (item) => {
    // Keep the main product/single-variant choice, but never guess a recommended garment's size.
    if (item.dataset.autoSelect !== 'true') return;
    const variants = getVariants(item);
    if (variants.length <= 1) return;

    const currentVariant = findVariant(item);
    const targetVariant = currentVariant?.available ? currentVariant : variants.find((variant) => variant.available);
    if (!targetVariant) return;

    item.querySelectorAll('[data-shop-the-look-option]').forEach((select, index) => {
      select.value = targetVariant.options?.[index] || '';
    });
  };

  const hasVariantSelection = (item) => {
    const variants = getVariants(item);
    if (variants.length === 1) return Boolean(variants[0]);
    return getSelectedOptions(item).length > 0 && getSelectedOptions(item).every(Boolean);
  };

  const isAvailableVariant = (item) => {
    const variants = getVariants(item);
    if (variants.length === 1) return Boolean(variants[0]?.available);
    return Boolean(findVariant(item)?.available);
  };

  const setStatus = (item, status) => {
    const statusElement = item.querySelector('[data-shop-the-look-status]');
    if (!statusElement) return;
    const available = item.dataset.statusAvailable || '';
    const soldOut = item.dataset.statusSoldOut || '';
    const alreadyInCart = item.dataset.statusAlreadyInCart || '';
    statusElement.textContent = status === 'available' ? available : status === 'sold-out' ? soldOut : status === 'already-in-cart' ? alreadyInCart : status;
  };

  const updateOptionAvailability = (item) => {
    const selects = Array.from(item.querySelectorAll('[data-shop-the-look-option]'));
    const variants = getVariants(item);
    selects.forEach((select, selectIndex) => {
      Array.from(select.options).forEach((option) => {
        if (!option.value) {
          option.disabled = false;
          return;
        }
        const otherOptions = selects.map((control) => control.value);
        otherOptions[selectIndex] = option.value;
        option.disabled = !variants.some((variant) => variant.available && variant.options?.every((value, index) => !otherOptions[index] || value === otherOptions[index]));
      });
    });
  };

  const updateItem = (item) => {
    const variants = getVariants(item);
    const variant = variants.length === 1 ? variants[0] : findVariant(item);
    item.dataset.selectedVariantId = variant?.id || '';
    const price = item.querySelector('[data-shop-the-look-price]');
    if (price) price.textContent = formatMoney(variant?.price ?? item.dataset.productPrice ?? 0);

    const checkbox = item.querySelector('[data-shop-the-look-checkbox]');
    const canBuy = Boolean(variant?.available);
    const hasSelection = hasVariantSelection(item);
    const isMain = item.dataset.mainProduct === 'true';
    checkbox.disabled = !canBuy || !hasSelection;

    if (!hasSelection && !isMain) {
      setStatus(item, item.dataset.statusChooseVariant || '');
    } else if (!canBuy) {
      setStatus(item, 'sold-out');
    } else {
      setStatus(item, 'available');
    }

    checkbox.checked = canBuy && hasSelection;
    updateOptionAvailability(item);
  };

  const syncMainVariant = (root, variantId) => {
    const main = root.querySelector('[data-shop-the-look-item][data-main-product="true"]');
    if (!main || !variantId) return;
    const variant = getVariants(main).find((item) => String(item.id) === String(variantId));
    if (!variant) return;
    main.querySelectorAll('[data-shop-the-look-option]').forEach((select, index) => {
      select.value = variant.options?.[index] || '';
    });
    main.dataset.selectedVariantId = variant.id;
    updateItem(main);
    root.dataset.mainVariantId = variant.id;
  };

  const showError = (root, message) => {
    const error = root.querySelector('[data-shop-the-look-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  };

  const cartItems = async () => {
    const response = await fetch(storefrontUrl('cart.js'), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('cart');
    return (await response.json()).items || [];
  };

  const refreshCartStatuses = async (root) => {
    try {
      const ids = new Set((await cartItems()).map((item) => String(item.variant_id)));
      root.querySelectorAll('[data-shop-the-look-item]').forEach((item) => {
        const variantId = item.dataset.selectedVariantId;
        if (variantId && ids.has(String(variantId))) {
          item.dataset.inCart = 'true';
          setStatus(item, 'already-in-cart');
        }
      });
    } catch (error) {
      // Cart state is only an enhancement; adding the look still handles duplicates server-side.
    }
  };

  const createRecommendationItem = (product, recommendation) => {
    const variants = (product.variants || []).map((variant) => ({
      id: variant.id,
      available: Boolean(variant.available),
      price: variant.price,
      options: variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean)
    }));
    const singleVariant = variants.length === 1 ? variants[0] : null;
    const item = document.createElement('article');
    item.className = 'shop-the-look__item';
    item.dataset.shopTheLookItem = '';
    item.dataset.productId = product.id;
    item.dataset.mainProduct = 'false';
    item.dataset.selectedVariantId = singleVariant?.id || '';
    item.dataset.autoSelect = variants.length === 1 ? 'true' : 'false';
    item.dataset.productAvailable = String(Boolean(product.available));
    item.dataset.productPrice = product.price || 0;
    item.dataset.statusAvailable = recommendation.dataset.statusAvailable || '';
    item.dataset.statusSoldOut = recommendation.dataset.statusSoldOut || '';
    item.dataset.statusChooseVariant = recommendation.dataset.statusChooseVariant || '';
    item.dataset.statusAlreadyInCart = recommendation.dataset.statusAlreadyInCart || '';

    const header = document.createElement('div');
    header.className = 'shop-the-look__item-header';
    const checkboxLabel = document.createElement('label');
    checkboxLabel.className = 'shop-the-look__checkbox-label';
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'shop-the-look__checkbox';
    checkbox.dataset.shopTheLookCheckbox = '';
    checkbox.checked = Boolean(singleVariant?.available);
    const checkboxUi = document.createElement('span');
    checkboxUi.className = 'shop-the-look__checkbox-ui';
    checkboxUi.setAttribute('aria-hidden', 'true');
    const checkboxText = document.createElement('span');
    checkboxText.className = 'sr-only';
    checkboxText.textContent = product.title;
    checkboxLabel.append(checkbox, checkboxUi, checkboxText);

    const imageLink = document.createElement('a');
    imageLink.className = 'shop-the-look__image';
    imageLink.href = storefrontUrl(`products/${encodeURIComponent(product.handle)}`);
    imageLink.setAttribute('data-instant', '');
    if (product.featured_image) {
      const image = document.createElement('img');
      image.src = product.featured_image.startsWith('//') ? `https:${product.featured_image}` : product.featured_image;
      image.alt = product.title;
      image.loading = 'lazy';
      image.sizes = '96px';
      imageLink.append(image);
    }

    const summary = document.createElement('div');
    summary.className = 'shop-the-look__item-summary';
    const title = document.createElement('a');
    title.className = 'shop-the-look__title product-title';
    title.href = imageLink.href;
    title.setAttribute('data-instant', '');
    title.textContent = product.title;
    const price = document.createElement('span');
    price.className = 'shop-the-look__price';
    price.dataset.shopTheLookPrice = '';
    const status = document.createElement('span');
    status.className = 'shop-the-look__status text-subdued';
    status.dataset.shopTheLookStatus = '';
    status.textContent = singleVariant?.available ? recommendation.dataset.statusAvailable : variants.length > 1 ? recommendation.dataset.statusChooseVariant : recommendation.dataset.statusSoldOut;
    summary.append(title, price, status);
    header.append(checkboxLabel, imageLink, summary);
    item.append(header);

    if (variants.length > 1 && product.options) {
      const options = document.createElement('div');
      options.className = 'shop-the-look__options';
      product.options.forEach((productOption) => {
        const label = document.createElement('label');
        label.className = 'shop-the-look__option';
        const labelText = document.createElement('span');
        labelText.className = 'shop-the-look__option-label';
        labelText.textContent = window.FendoSizeOptions?.displayLabel(productOption.name, getStorefrontLocale()) || productOption.name;
        const select = document.createElement('select');
        select.className = 'shop-the-look__select';
        select.dataset.shopTheLookOption = '';
        select.dataset.optionPosition = productOption.position;
        select.setAttribute('aria-label', labelText.textContent);
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = recommendation.dataset.chooseVariant || '';
        select.append(placeholder);
        productOption.values.forEach((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          select.append(option);
        });
        label.append(labelText, select);
        options.append(label);
      });
      summary.append(options);
    }

    const variantScript = document.createElement('script');
    variantScript.type = 'application/json';
    variantScript.dataset.shopTheLookVariants = '';
    variantScript.textContent = JSON.stringify(variants);
    item.append(variantScript);
    return item;
  };

  const loadRecommendations = async (recommendations) => {
    if (recommendations.dataset.loading === 'true' || recommendations.dataset.loaded === 'true') return;
    recommendations.dataset.loading = 'true';
    try {
      const recommendationUrls = getRecommendationUrls(recommendations.dataset.recommendationsUrl);
      if (recommendationUrls.length === 0) return;
      let payload = null;
      for (const recommendationsUrl of recommendationUrls) {
        try {
          const response = await fetch(recommendationsUrl, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            cache: 'no-store'
          });
          if (!response.ok) continue;
          const candidate = await response.json();
          if (Array.isArray(candidate.products)) {
            payload = candidate;
            break;
          }
        } catch (error) {
          // Try the next storefront path.
        }
      }
      if (!payload) return;
      const products = Array.isArray(payload.products) ? payload.products : [];
      if (products.length === 0) return;
      const items = recommendations.querySelector('[data-shop-the-look-items]');
      products.forEach((product) => items?.append(createRecommendationItem(product, recommendations)));
      const shell = recommendations.querySelector('[data-shop-the-look-shell]');
      if (!items || !shell) return;
      shell.hidden = false;
      recommendations.dataset.loaded = 'true';
      initialize(recommendations);
    } catch (error) {
      // Keep the CTA visible; recommendations can be retried when the drawer is opened.
    } finally {
      recommendations.dataset.loading = 'false';
    }
  };

  const collectLines = (root, fullLook) => {
    const lines = [];
    root.querySelectorAll('[data-shop-the-look-item]').forEach((item) => {
      const checkbox = item.querySelector('[data-shop-the-look-checkbox]');
      const variants = getVariants(item);
      const variant = variants.length === 1 ? variants[0] : findVariant(item);
      if (!variant || !variant.available) return;
      if (fullLook || checkbox?.checked) lines.push({ id: Number(variant.id), quantity: 1, title: item.querySelector('.shop-the-look__title')?.textContent.trim() || '' });
    });
    return lines;
  };

  const getActionButton = (root, selector) => root.querySelector(selector) || root.closest('x-drawer')?.querySelector(selector);

  const addToCart = async (root, fullLook) => {
    if (inFlightRoots.has(root)) return;
    const addButton = getActionButton(root, fullLook ? '[data-shop-the-look-add-full]' : '[data-shop-the-look-add-selected]');
    const drawer = root.closest('x-drawer');
    const items = collectLines(root, fullLook);
    showError(root, '');
    if (items.length === 0) {
      showError(root, root.dataset.errorNoSelection || '');
      return;
    }

    const missingVariant = Array.from(root.querySelectorAll('[data-shop-the-look-item]')).some((item) => {
      const checkbox = item.querySelector('[data-shop-the-look-checkbox]');
      return (fullLook || checkbox?.checked) && item.dataset.productAvailable === 'true' && getVariants(item).length > 1 && !isAvailableVariant(item);
    });
    if (missingVariant) {
      showError(root, root.dataset.errorChooseVariant || '');
      return;
    }

    // One request at a time for the whole drawer, including keyboard activation.
    inFlightRoots.add(root);
    const actionScope = drawer || root;
    const buttonStates = Array.from(actionScope.querySelectorAll(
      '[data-shop-the-look-add-selected], [data-shop-the-look-add-full]'
    )).map((button) => ({ button, disabled: button.disabled, busy: button.getAttribute('aria-busy') }));
    for (const { button } of buttonStates) button.disabled = true;
    addButton?.setAttribute('aria-busy', 'true');
    try {
      const existingItems = await cartItems();
      const existingIds = new Set(existingItems.map((item) => String(item.variant_id)));
      const newItems = items.filter((item) => !existingIds.has(String(item.id)));
      if (newItems.length === 0) {
        showError(root, root.dataset.errorAlreadyInCart || '');
        return;
      }

      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
      const response = await fetch(storefrontUrl('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: newItems.map(({ id, quantity }) => ({ id, quantity })),
          sections: sections.join(','),
          sections_url: `${window.location.pathname}${window.location.search}`
        })
      });
      const responseJson = await response.json();
      if (!response.ok) throw new Error(responseJson.description || root.dataset.errorGeneric || '');

      const cart = await (await fetch(storefrontUrl('cart.js'), { headers: { Accept: 'application/json' } })).json();
      cart.sections = responseJson.sections || {};
      if (window.themeVariables?.settings?.cartType === 'drawer') {
        await Promise.all(sections.filter((section) => !cart.sections[section]).map(async (section) => {
          const sectionResponse = await fetch(`${storefrontUrl()}?section_id=${encodeURIComponent(section)}`);
          if (sectionResponse.ok) cart.sections[section] = await sectionResponse.text();
        }));
      }
      await drawer?.hide();
      if (window.themeVariables?.settings?.cartType === 'drawer') {
        document.documentElement.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: Array.isArray(responseJson.items) ? responseJson.items : [responseJson],
            cart,
            onSuccessDo: 'force_open_drawer'
          }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart }
        }));
      } else {
        window.location.href = storefrontUrl('cart');
      }
    } catch (error) {
      showError(root, error.message || root.dataset.errorGeneric || '');
    } finally {
      for (const { button, disabled, busy } of buttonStates) {
        button.disabled = disabled;
        if (busy === null) button.removeAttribute('aria-busy');
        else button.setAttribute('aria-busy', busy);
      }
      inFlightRoots.delete(root);
    }
  };

  const initialize = (recommendations) => {
    const root = recommendations.querySelector('[data-shop-the-look]');
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    root.dataset.errorNoSelection = recommendations.dataset.errorNoSelection || '';
    root.dataset.errorChooseVariant = recommendations.dataset.errorChooseVariant || '';
    root.dataset.errorAlreadyInCart = recommendations.dataset.errorAlreadyInCart || '';
    root.dataset.errorGeneric = recommendations.dataset.errorGeneric || '';
    root.querySelectorAll('[data-shop-the-look-item]').forEach((item) => {
      selectFirstAvailableVariant(item);
      updateItem(item);
    });
    root.addEventListener('change', (event) => {
      const item = event.target.closest('[data-shop-the-look-item]');
      if (item && event.target.matches('[data-shop-the-look-option]')) updateItem(item);
      updateSubtotal(root);
    });
    getActionButton(root, '[data-shop-the-look-add-selected]')?.addEventListener('click', () => addToCart(root, false));
    getActionButton(root, '[data-shop-the-look-add-full]')?.addEventListener('click', () => addToCart(root, true));
    root.querySelectorAll('[data-shop-the-look-item]').forEach((item) => item.querySelector('[data-shop-the-look-checkbox]')?.addEventListener('change', () => updateSubtotal(root)));
    root.closest('x-drawer')?.addEventListener('dialog:after-show', () => refreshCartStatuses(root));
    updateSubtotal(root);
  };

  const updateSubtotal = (root) => {
    let subtotal = 0;
    root.querySelectorAll('[data-shop-the-look-item]').forEach((item) => {
      if (!item.querySelector('[data-shop-the-look-checkbox]')?.checked) return;
      const variants = getVariants(item);
      const variant = variants.length === 1 ? variants[0] : findVariant(item);
      if (variant?.available) subtotal += Number(variant.price || 0);
    });
    const target = root.querySelector('[data-shop-the-look-subtotal]') || root.closest('x-drawer')?.querySelector('[data-shop-the-look-subtotal]');
    if (target) target.textContent = formatMoney(subtotal);
  };

  const initAll = (scope = document) => {
    const recommendations = scope.matches?.(recommendationSelector)
      ? [scope, ...scope.querySelectorAll(recommendationSelector)]
      : Array.from(scope.querySelectorAll(recommendationSelector));
    recommendations.forEach(loadRecommendations);
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('.shop-the-look__trigger');
    const recommendations = trigger?.closest('[data-shop-the-look-recommendations]');
    if (recommendations) loadRecommendations(recommendations);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }
  document.addEventListener('variant:change', (event) => {
    const form = event.target;
    const root = form?.closest('product-rerender')?.querySelector('[data-shop-the-look]') || document.querySelector('[data-shop-the-look]');
    if (root && event.detail?.variant?.id) syncMainVariant(root, event.detail.variant.id);
  });
  const observer = new MutationObserver((mutations) => mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => node.nodeType === Node.ELEMENT_NODE && initAll(node))));
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
